import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Container,
  Input,
  Button,
  VStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Spinner,
  Card,
  CardBody,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Text,
  FormHelperText,
  Alert,
  AlertIcon,
  HStack,
  Divider,
  Box,
  SimpleGrid,
  Badge,
  Wrap,
  WrapItem,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  CheckboxGroup,
  Checkbox,
  Switch,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tag,
  LinkBox,
  LinkOverlay
} from '@chakra-ui/react'
import { ChevronRightIcon, ChevronDownIcon } from '@chakra-ui/icons'
import { 
  FiGlobe, 
  FiMapPin, 
  FiStar, 
  FiPlus, 
  FiTrash2, 
  FiEdit3,
  FiCheck,
  FiX,
  FiSettings,
  FiMoreVertical,
  FiFileText,
  FiEye,
  FiClock,
  FiUsers,
  FiLayers
} from 'react-icons/fi'
import { Link as RouterLink } from 'react-router-dom'
import apiClient from '../../../utils/apiClient'

const EditSite = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Modal controls
  const { isOpen: isLocaleModalOpen, onOpen: onLocaleModalOpen, onClose: onLocaleModalClose } = useDisclosure()

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0] || null

  // State for site management
  const [site, setSite] = useState({
    id: null,
    name: '',
    domain: '',
    site_status: 'inactive',
    description: '',
    default_locale_id: null,
    assignedLocales: [],
    defaultLocale: null,
    activeLocalesCount: 0
  })
  const [availableLocales, setAvailableLocales] = useState([])
  const [selectedNewLocales, setSelectedNewLocales] = useState([])
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [localeActionLoading, setLocaleActionLoading] = useState({})
  const [actionLoading, setActionLoading] = useState({})

  // State for content management
  const [contentEntries, setContentEntries] = useState([])
  const [contentModels, setContentModels] = useState([])
  const [contentLoading, setContentLoading] = useState(false)
  const [selectedLocaleForContent, setSelectedLocaleForContent] = useState(null)

  useEffect(() => {
    const fetchSite = async () => {
      try {
        const siteData = await apiClient(`/sites/${tenantId}/${id}`)
        setSite(siteData)
        
        // Set default locale for content if available
        if (siteData.defaultLocale) {
          setSelectedLocaleForContent(siteData.defaultLocale.locale_id)
        }
      } catch (error) {
        toast({
          title: 'Error fetching site',
          description: error.message,
          status: 'error',
          duration: 3000
        })
      }
    }

    const fetchAvailableLocales = async () => {
      try {
        const localesData = await apiClient(`/sites/${tenantId}/${id}/available-locales`)
        setAvailableLocales(localesData)
      } catch (error) {
        toast({
          title: 'Error fetching available locales',
          description: error.message,
          status: 'error',
          duration: 3000
        })
      }
    }

    const fetchContentModels = async () => {
      try {
        const modelsData = await apiClient(`/content-models/${tenantId}`)
        setContentModels(modelsData)
      } catch (error) {
        console.error('Error fetching content models:', error)
      }
    }

    const fetchData = async () => {
      await Promise.all([fetchSite(), fetchAvailableLocales(), fetchContentModels()])
      setLoading(false)
    }

    fetchData()
  }, [tenantId, id])
  // Fetch content entries when locale selection changes
  useEffect(() => {
    if (selectedLocaleForContent && site.id) {
      fetchContentEntries()
    }
  }, [selectedLocaleForContent, site.id])

  const fetchContentEntries = async () => {
    if (!selectedLocaleForContent || !site || !site.id) return;
    console.log('fetching content entries', selectedLocaleForContent, site.id)
    setContentLoading(true)
    try {
      const entriesData = await apiClient(`/content-entries/${tenantId}/${site.id}?locale_id=${selectedLocaleForContent}&limit=10`)
      console.log('entriesData', entriesData.data)
      const normalizedEntries = Array.isArray(entriesData.data) ? entriesData.data : [entriesData.data];
      setContentEntries(normalizedEntries)
    } catch (error) {
      console.error('Error fetching content entries:', error)
      toast({
        title: 'Error fetching content',
        description: error.message,
        status: 'error',
        duration: 3000
      })
    } finally {
      setContentLoading(false)
    }
  }

  const handleSiteChange = (e) => {
    const { name, value } = e.target
    setSite(prev => ({ ...prev, [name]: value }))
  }

  const handleSiteUpdate = async (e) => {
    e.preventDefault()
    
    if (isSubmitting) return
    
    setIsSubmitting(true)
    
    try {
      await apiClient(`/sites/${tenantId}/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: site.name,
          domain: site.domain,
          site_status: site.site_status,
          description: site.description,
          default_locale_id: site.default_locale_id
        })
      })

      toast({
        title: 'Success',
        description: 'Site updated successfully',
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update site',
        status: 'error',
        duration: 3000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAssignLocales = async () => {
    if (selectedNewLocales.length === 0) return

    setLocaleActionLoading({ assign: true })
    
    try {
      const assignments = selectedNewLocales.map(localeId =>
        apiClient(`/sites/${tenantId}/${id}/locales`, {
          method: 'POST',
          body: JSON.stringify({
            locale_id: parseInt(localeId),
            is_default: false,
            is_active: true
          })
        })
      )

      await Promise.all(assignments)

      // Refresh site data
      const updatedSite = await apiClient(`/sites/${tenantId}/${id}`)
      setSite(updatedSite)
      
      // Refresh available locales
      const updatedAvailableLocales = await apiClient(`/sites/${tenantId}/${id}/available-locales`)
      setAvailableLocales(updatedAvailableLocales)

      setSelectedNewLocales([])
      onLocaleModalClose()

      toast({
        title: 'Success',
        description: `${selectedNewLocales.length} locale(s) assigned successfully`,
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to assign locales',
        status: 'error',
        duration: 3000
      })
    } finally {
      setLocaleActionLoading({ assign: false })
    }
  }

  const handleRemoveLocale = async (localeId) => {
    setLocaleActionLoading({ [`remove_${localeId}`]: true })
    
    try {
      await apiClient(`/sites/${tenantId}/${id}/locales/${localeId}`, {
        method: 'DELETE'
      })

      // Refresh site data
      const updatedSite = await apiClient(`/sites/${tenantId}/${id}`)
      setSite(updatedSite)
      
      // Refresh available locales
      const updatedAvailableLocales = await apiClient(`/sites/${tenantId}/${id}/available-locales`)
      setAvailableLocales(updatedAvailableLocales)

      toast({
        title: 'Success',
        description: 'Locale removed successfully',
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove locale',
        status: 'error',
        duration: 3000
      })
    } finally {
      setLocaleActionLoading({ [`remove_${localeId}`]: false })
    }
  }

  const handleSetDefaultLocale = async (localeId) => {
    setLocaleActionLoading({ [`default_${localeId}`]: true })
    
    try {
      await apiClient(`/sites/${tenantId}/${id}/locales/${localeId}/set-default`, {
        method: 'PUT'
      })

      // Refresh site data
      const updatedSite = await apiClient(`/sites/${tenantId}/${id}`)
      setSite(updatedSite)

      toast({
        title: 'Success',
        description: 'Default locale updated successfully',
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to set default locale',
        status: 'error',
        duration: 3000
      })
    } finally {
      setLocaleActionLoading({ [`default_${localeId}`]: false })
    }
  }

  const handleToggleLocaleActive = async (localeId) => {
    setLocaleActionLoading({ [`toggle_${localeId}`]: true })
    
    try {
      await apiClient(`/sites/${tenantId}/${id}/locales/${localeId}/toggle-active`, {
        method: 'PUT'
      })

      // Refresh site data
      const updatedSite = await apiClient(`/sites/${tenantId}/${id}`)
      setSite(updatedSite)

      toast({
        title: 'Success',
        description: 'Locale status updated successfully',
        status: 'success',
        duration: 3000
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle locale status',
        status: 'error',
        duration: 3000
      })
    } finally {
      setLocaleActionLoading({ [`toggle_${localeId}`]: false })
    }
  }

  const handleCreateContent = () => {
    navigate(`/content/create?site_id=${site.id}&locale_id=${selectedLocaleForContent}`)
  }

  const handleEditContent = (contentId) => {
    navigate(`/content/edit/${contentId}`)
  }

  const handleDeleteContent = async (contentId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return
    }

    try {
      await apiClient(`/content-entries/${tenantId}/${contentId}`, {
        method: 'DELETE'
      })

      toast({
        title: 'Success',
        description: 'Content deleted successfully',
        status: 'success',
        duration: 3000
      })

      // Refresh content entries
      fetchContentEntries()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete content',
        status: 'error',
        duration: 3000
      })
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'green'
      case 'draft': return 'orange'
      case 'archived': return 'gray'
      default: return 'gray'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const isFormValid = site.name.trim() && site.domain.trim()

  if (loading) {
    return (
      <Container maxW="container.xl" py={6}>
        <VStack align="center" justify="center" height="400px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading site information...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="container.xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/sites">Sites</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Page Header */}
        <Box>
          <HStack justify="space-between" align="center" mb={2}>
            <HStack>
              <Icon as={FiGlobe} color="blue.500" boxSize={6} />
              <Heading size="lg">Edit Site</Heading>
            </HStack>
            <Badge colorScheme="blue" variant="subtle">
              ID: {site && site.id ? site.id : <Spinner size="sm" />}
            </Badge>
          </HStack>
          <Text color="gray.500">
            Manage site settings, languages, and content for your multilingual website.
          </Text>
        </Box>

        {/* Main Content Tabs */}
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>
              <HStack>
                <Icon as={FiSettings} />
                <Text>Site Settings</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <Icon as={FiMapPin} />
                <Text>Languages</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <Icon as={FiFileText} />
                <Text>Content</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Site Settings Tab */}
            <TabPanel px={0}>
              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody>
                  <form onSubmit={handleSiteUpdate}>
                    <VStack spacing={6} align="stretch">
                      <Box>
                        <Heading size="md" mb={4}>Site Information</Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                          <FormControl isRequired>
                            <FormLabel>Site Name</FormLabel>
                            <Input
                              name="name"
                              value={site.name}
                              onChange={handleSiteChange}
                              placeholder="Enter site name"
                            />
                            <FormHelperText>A descriptive name for your site</FormHelperText>
                          </FormControl>

                          <FormControl isRequired>
                            <FormLabel>Domain</FormLabel>
                            <Input
                              name="domain"
                              value={site.domain}
                              onChange={handleSiteChange}
                              placeholder="example.com"
                            />
                            <FormHelperText>The domain where this site will be accessed</FormHelperText>
                          </FormControl>
                        </SimpleGrid>

                        <FormControl mt={6}>
                          <FormLabel>Status</FormLabel>
                          <Select name="site_status" value={site.site_status} onChange={handleSiteChange}>
                            <option value="draft">Draft</option>
                            <option value="inactive">Inactive</option>
                            <option value="active">Active</option>
                            <option value="maintenance">Maintenance</option>
                          </Select>
                          <FormHelperText>
                            Set to "Active" when the site is ready to receive traffic
                          </FormHelperText>
                        </FormControl>

                        <FormControl mt={6}>
                          <FormLabel>Description</FormLabel>
                          <Textarea
                            name="description"
                            value={site.description || ''}
                            onChange={handleSiteChange}
                            placeholder="Brief description of this site..."
                            rows={3}
                          />
                          <FormHelperText>
                            Optional description for internal reference
                          </FormHelperText>
                        </FormControl>
                      </Box>

                      <Divider />

                      <HStack justify="flex-end">
                        <Button
                          type="submit"
                          colorScheme="blue"
                          isDisabled={!isFormValid}
                          isLoading={isSubmitting}
                          loadingText="Updating..."
                        >
                          Update Site Information
                        </Button>
                      </HStack>
                    </VStack>
                  </form>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Languages Tab */}
            <TabPanel px={0}>
              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <HStack justify="space-between" align="center">
                      <HStack>
                        <Icon as={FiMapPin} color="purple.500" />
                        <Heading size="md">Language Management</Heading>
                      </HStack>
                      <Button
                        leftIcon={<FiPlus />}
                        colorScheme="purple"
                        variant="outline"
                        size="sm"
                        onClick={onLocaleModalOpen}
                        isDisabled={availableLocales.length === 0}
                      >
                        Assign Languages
                      </Button>
                    </HStack>

                    {/* Language Statistics */}
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <Card variant="outline" size="sm">
                        <CardBody>
                          <Stat>
                            <StatLabel>Total Languages</StatLabel>
                            <StatNumber color="blue.500">{site.assignedLocales?.length || 0}</StatNumber>
                            <StatHelpText>Assigned to this site</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                      
                      <Card variant="outline" size="sm">
                        <CardBody>
                          <Stat>
                            <StatLabel>Active Languages</StatLabel>
                            <StatNumber color="green.500">{site.activeLocalesCount || 0}</StatNumber>
                            <StatHelpText>Currently enabled</StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                      
                      <Card variant="outline" size="sm">
                        <CardBody>
                          <Stat>
                            <StatLabel>Default Language</StatLabel>
                            <StatNumber color="yellow.500">
                              {site.defaultLocale ? '1' : '0'}
                            </StatNumber>
                            <StatHelpText>
                              {site.defaultLocale ? site.defaultLocale.locale_code : 'Not set'}
                            </StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </SimpleGrid>

                    {/* Current Languages */}
                    {site.assignedLocales && site.assignedLocales.length > 0 ? (
                      <Box>
                        <Text fontWeight="medium" mb={3}>Configured Languages</Text>
                        <VStack spacing={3} align="stretch">
                          {site.assignedLocales.map(locale => (
                            <Card key={locale.locale_id} variant="outline" size="sm">
                              <CardBody>
                                <HStack justify="space-between" align="center">
                                  <HStack spacing={3}>
                                    <Badge 
                                      colorScheme={locale.is_default ? 'yellow' : locale.is_active ? 'green' : 'gray'}
                                      variant={locale.is_default ? 'solid' : 'subtle'}
                                    >
                                      {locale.locale_code}
                                      {locale.is_default && ' ★'}
                                    </Badge>
                                    <VStack align="start" spacing={0}>
                                      <HStack spacing={1}>
                                        <Text fontWeight="medium" fontSize="sm">
                                          {locale.locale_name}
                                        </Text>
                                        <Tooltip label={locale.is_default ? 'Remove as Default' : 'Set as Default'}>
                                          <IconButton
                                            icon={<FiStar />}
                                            variant="ghost"
                                            size="xs"
                                            color={locale.is_default ? 'yellow.500' : 'gray.400'}
                                            onClick={() => handleSetDefaultLocale(locale.locale_id)}
                                            isLoading={actionLoading[`default_${locale.locale_id}`]}
                                            aria-label={locale.is_default ? 'Remove as Default' : 'Set as Default'}
                                          />
                                        </Tooltip>
                                      </HStack>
                                      <HStack spacing={2}>
                                        {locale.is_default && (
                                          <Badge size="xs" colorScheme="yellow" variant="outline">
                                            Default
                                          </Badge>
                                        )}
                                        <Badge 
                                          size="xs" 
                                          colorScheme={locale.is_active ? 'green' : 'gray'}
                                          variant="outline"
                                        >
                                          {locale.is_active ? 'Active' : 'Inactive'}
                                        </Badge>
                                      </HStack>
                                    </VStack>
                                  </HStack>

                                  <HStack>
                                    <Tooltip label={locale.is_active ? 'Deactivate' : 'Activate'}>
                                      <Switch
                                        isChecked={locale.is_active}
                                        onChange={() => handleToggleLocaleActive(locale.locale_id)}
                                        isDisabled={localeActionLoading[`toggle_${locale.locale_id}`]}
                                        colorScheme="green"
                                        size="sm"
                                      />
                                    </Tooltip>

                                    <Menu>
                                      <MenuButton
                                        as={IconButton}
                                        icon={<FiMoreVertical />}
                                        variant="ghost"
                                        size="sm"
                                        isLoading={localeActionLoading[`default_${locale.locale_id}`]}
                                      />
                                      <MenuList>
                                        {!locale.is_default && (
                                          <MenuItem
                                            icon={<FiStar />}
                                            onClick={() => handleSetDefaultLocale(locale.locale_id)}
                                          >
                                            Set as Default
                                          </MenuItem>
                                        )}
                                        <MenuItem
                                          icon={<FiTrash2 />}
                                          color="red.500"
                                          onClick={() => handleRemoveLocale(locale.locale_id)}
                                          isDisabled={locale.is_default && site.assignedLocales.length === 1}
                                        >
                                          Remove Language
                                        </MenuItem>
                                      </MenuList>
                                    </Menu>
                                  </HStack>
                                </HStack>
                              </CardBody>
                            </Card>
                          ))}
                        </VStack>
                      </Box>
                    ) : (
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">No Languages Configured</Text>
                          <Text fontSize="sm">
                            Assign languages to enable multilingual support for this site.
                          </Text>
                        </VStack>
                      </Alert>
                    )}

                    {/* Warning if no default locale */}
                    {site.assignedLocales && site.assignedLocales.length > 0 && !site.defaultLocale && (
                      <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">No Default Language Set</Text>
                          <Text fontSize="sm">
                            Please set a default language to ensure proper fallback behavior for content.
                          </Text>
                        </VStack>
                      </Alert>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Content Tab */}
            <TabPanel px={0}>
              <Card bg={cardBg} borderColor={borderColor} variant="outline">
                <CardBody>
                  <VStack spacing={6} align="stretch">
                    <HStack justify="space-between" align="center">
                      <HStack>
                        <Icon as={FiFileText} color="green.500" />
                        <Heading size="md">Content Management</Heading>
                      </HStack>
                      <HStack>
                        {site.assignedLocales && site.assignedLocales.length > 0 && (
                          <FormControl maxW="200px">
                            <Select
                              value={selectedLocaleForContent || ''}
                              onChange={(e) => setSelectedLocaleForContent(e.target.value ? parseInt(e.target.value) : null)}
                              placeholder="Select Language"
                              size="sm"
                            >
                              {site.assignedLocales
                                .filter(locale => locale.is_active)
                                .map(locale => (
                                  <option key={locale.locale_id} value={locale.locale_id}>
                                    {locale.locale_name} ({locale.locale_code})
                                  </option>
                                ))}
                            </Select>
                          </FormControl>
                        )}
                        <Button
                          leftIcon={<FiPlus />}
                          colorScheme="green"
                          size="sm"
                          onClick={handleCreateContent}
                          isDisabled={!selectedLocaleForContent}
                        >
                          Create Content
                        </Button>
                      </HStack>
                    </HStack>

                    {/* Content Overview */}
                    {console.log('contentEntries', contentEntries)}
                    {console.log('selectedLocaleForContent', selectedLocaleForContent)}
                    
                    {selectedLocaleForContent ? (
                      <Box>
                        {contentLoading ? (
                          <VStack align="center" py={8}>
                            <Spinner size="lg" />
                            <Text>Loading content...</Text>
                          </VStack>
                        ) : contentEntries.length > 0 ? (
                          <TableContainer>
                            <Table variant="simple" size="sm">
                              <Thead>
                                <Tr>
                                  <Th>Title</Th>
                                  <Th>Content Model</Th>
                                  <Th>Status</Th>
                                  <Th>Last Updated</Th>
                                  <Th>Actions</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {contentEntries.map(entry => (
                                  <Tr key={entry.id}>
                                    <Td>
                                      <VStack align="start" spacing={1}>
                                        <Text fontWeight="medium" fontSize="sm">
                                          {entry.title || entry.default_title || 'Untitled'}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                          {entry.slug}
                                        </Text>
                                      </VStack>
                                    </Td>
                                    <Td>
                                      <Badge colorScheme="blue" variant="subtle" size="sm">
                                        {entry.content_model_name || 'Unknown'}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      <Badge
                                        colorScheme={getStatusColor(entry.translation_status || entry.status)}
                                        variant="subtle"
                                        size="sm"
                                      >
                                        {(entry.translation_status || entry.status || 'draft').toUpperCase()}
                                      </Badge>
                                    </Td>
                                    <Td>
                                      <Text fontSize="xs" color="gray.500">
                                        {formatDate(entry.updated_at)}
                                      </Text>
                                    </Td>
                                    <Td>
                                      <HStack spacing={1}>
                                        <Tooltip label="Edit">
                                          <IconButton
                                            icon={<FiEdit3 />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => handleEditContent(entry.id)}
                                          />
                                        </Tooltip>
                                        <Tooltip label="Delete">
                                          <IconButton
                                            icon={<FiTrash2 />}
                                            size="xs"
                                            variant="ghost"
                                            colorScheme="red"
                                            onClick={() => handleDeleteContent(entry.id, entry.title || entry.default_title)}
                                          />
                                        </Tooltip>
                                      </HStack>
                                    </Td>
                                  </Tr>
                                ))}
                              </Tbody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="medium">No Content Found</Text>
                              <Text fontSize="sm">
                                Create your first piece of content for this language.
                              </Text>
                            </VStack>
                          </Alert>
                        )}
                      </Box>
                    ) : (
                      <Alert status="warning" borderRadius="md">
                        <AlertIcon />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">Select a Language</Text>
                          <Text fontSize="sm">
                            Choose a language to view and manage content for this site.
                          </Text>
                        </VStack>
                      </Alert>
                    )}
                  </VStack>
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Assign Locales Modal */}
      <Modal isOpen={isLocaleModalOpen} onClose={onLocaleModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Languages to Site</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="gray.500" fontSize="sm">
                Select languages to assign to this site. You can manage their settings after assignment.
              </Text>
              
              {availableLocales.length > 0 ? (
                <FormControl>
                  <FormLabel>Available Languages ({selectedNewLocales.length} selected)</FormLabel>
                  <CheckboxGroup 
                    value={selectedNewLocales} 
                    onChange={setSelectedNewLocales}
                  >
                    <SimpleGrid columns={1} spacing={2}>
                      {availableLocales.map(locale => (
                        <Card
                          key={locale.id}
                          variant="outline"
                          size="sm"
                          borderColor={selectedNewLocales.includes(locale.id.toString()) ? "blue.200" : borderColor}
                          bg={selectedNewLocales.includes(locale.id.toString()) ? "blue.50" : "transparent"}
                        >
                          <CardBody p={3}>
                            <Checkbox
                              value={locale.id.toString()}
                              w="full"
                            >
                              <HStack justify="space-between" w="full">
                                <VStack align="start" spacing={1}>
                                  <Text fontWeight="medium" fontSize="sm">
                                    {locale.name}
                                  </Text>
                                  <Badge size="sm" colorScheme="blue" variant="subtle">
                                    {locale.code}
                                  </Badge>
                                </VStack>
                              </HStack>
                            </Checkbox>
                          </CardBody>
                        </Card>
                      ))}
                    </SimpleGrid>
                  </CheckboxGroup>
                </FormControl>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <Text>All available languages are already assigned to this site.</Text>
                </Alert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onLocaleModalClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleAssignLocales}
              isDisabled={selectedNewLocales.length === 0}
              isLoading={localeActionLoading.assign}
              loadingText="Assigning..."
            >
              Assign Languages
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  )
}

export default EditSite